import * as THREE from 'three';
import { createToonGradientMap, toonMat } from './materials.js';

export const NPC_VISIT_X = -20;
export const NPC_VISIT_Z = -26;
export const VISIT_QUEST_MAT_REWARD = 50;
export const VISIT_QUEST_SHIELD_REWARD = 30;

const NEAR_RADIUS = 4.5;
const NEAR_RADIUS_SQ = NEAR_RADIUS * NEAR_RADIUS;
const INTERACT_RANGE = 4.2;
const LOOK_DOT_MIN = 0.78;

/** POI names the player must enter during an accepted quest. */
export const VISIT_QUEST_POIS = ['PLEASANT PARK', 'TILTED TOWERS'] as const;

export type NpcVisitQuestState = 'available' | 'accepted' | 'completed';

/** Quest giver at Lucky Landing — visit two POIs per match. */
export class FpsNpcQuestVisit {
  private readonly group: THREE.Group;
  private readonly markerSprite: THREE.Sprite;
  private readonly backdrop: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly titleEl: HTMLDivElement;
  private readonly bodyEl: HTMLDivElement;
  private readonly actionBtn: HTMLButtonElement;
  private readonly toonGrad: THREE.DataTexture;
  private visible = false;
  private state: NpcVisitQuestState = 'available';
  private readonly visited = new Set<string>();
  private bobPhase = Math.random() * Math.PI * 2;

  constructor(
    sceneRoot: THREE.Group,
    mount: HTMLElement,
    private readonly onAccept: () => void,
    private readonly onComplete: () => void,
  ) {
    this.toonGrad = createToonGradientMap();
    this.group = new THREE.Group();
    this.group.position.set(NPC_VISIT_X, 0, NPC_VISIT_Z);
    this.group.rotation.y = Math.PI * 0.2;
    this.buildNpcMesh();
    this.markerSprite = this.createQuestMarker();
    this.group.add(this.markerSprite);
    sceneRoot.add(this.group);

    this.backdrop = document.createElement('div');
    this.backdrop.style.cssText =
      'position:fixed;inset:0;z-index:95;display:none;align-items:center;justify-content:center;' +
      'background:rgba(6,10,16,0.58);backdrop-filter:blur(7px);pointer-events:auto';

    this.panel = document.createElement('div');
    this.panel.style.cssText =
      'min-width:300px;padding:18px 20px 16px;border-radius:14px;' +
      'background:linear-gradient(165deg,rgba(24,28,42,0.97),rgba(10,14,24,0.98));' +
      'border:2px solid rgba(100,180,255,0.62);color:#e8f4ff;font:14px system-ui,sans-serif';

    this.titleEl = document.createElement('div');
    this.titleEl.style.cssText = 'font-weight:700;font-size:15px;color:#88ccff;margin-bottom:4px';
    this.titleEl.textContent = 'Cartographer Ray';
    this.panel.appendChild(this.titleEl);

    const sub = document.createElement('div');
    sub.textContent = 'Lucky Landing';
    sub.style.cssText = 'opacity:0.72;font-size:12px;margin-bottom:12px';
    this.panel.appendChild(sub);

    this.bodyEl = document.createElement('div');
    this.bodyEl.style.cssText = 'margin-bottom:14px;font-size:13px;line-height:1.45;color:#d8e8ff';
    this.panel.appendChild(this.bodyEl);

    this.actionBtn = document.createElement('button');
    this.actionBtn.type = 'button';
    this.actionBtn.style.cssText =
      'width:100%;padding:10px 12px;border-radius:10px;border:1px solid rgba(100,180,255,0.45);' +
      'background:rgba(12,22,38,0.88);color:#c8e8ff;font:700 13px system-ui;cursor:pointer';
    this.actionBtn.addEventListener('click', () => this.handleAction());
    this.panel.appendChild(this.actionBtn);

    this.backdrop.appendChild(this.panel);
    mount.appendChild(this.backdrop);
    this.refreshDialog();
  }

  private buildNpcMesh(): void {
    const coat = toonMat(0x5588cc, this.toonGrad, { emissive: 0x182840, emissiveIntensity: 0.2 });
    const skin = toonMat(0xd4a574, this.toonGrad);
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.72, 0.32), coat);
    body.position.y = 1.08;
    body.castShadow = true;
    this.group.add(body);
    const head = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.34, 0.34), skin);
    head.position.y = 1.62;
    this.group.add(head);
    const map = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.02, 0.28), toonMat(0xf5e6a8, this.toonGrad));
    map.position.set(0.28, 1.05, 0.22);
    map.rotation.y = -0.4;
    this.group.add(map);
  }

  private createQuestMarker(): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#66bbff';
    ctx.beginPath();
    ctx.arc(32, 32, 28, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#0a1830';
    ctx.font = 'bold 34px system-ui,sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('?', 32, 34);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }));
    sprite.scale.set(0.55, 0.55, 1);
    sprite.position.y = 2.15;
    return sprite;
  }

  resetForMatch(): void {
    this.state = 'available';
    this.visited.clear();
    this.close();
    this.markerSprite.visible = true;
    this.refreshDialog();
  }

  getState(): NpcVisitQuestState {
    return this.state;
  }

  getHudText(): string {
    if (this.state !== 'accepted') return '';
    const done = VISIT_QUEST_POIS.filter((p) => this.visited.has(p)).length;
    return `Quest: Visit POIs ${done}/${VISIT_QUEST_POIS.length}`;
  }

  isNear(px: number, pz: number): boolean {
    const dx = px - NPC_VISIT_X;
    const dz = pz - NPC_VISIT_Z;
    return dx * dx + dz * dz <= NEAR_RADIUS_SQ;
  }

  getNearHint(): string {
    if (this.state === 'completed') return '';
    return this.state === 'accepted' ? '[E] Quest progress' : '[E] Cartographer Ray';
  }

  shouldShowPrompt(playerPos: THREE.Vector3, cameraDir: THREE.Vector3): boolean {
    if (this.state === 'completed') return false;
    const center = new THREE.Vector3(NPC_VISIT_X, 1.2, NPC_VISIT_Z);
    const toNpc = center.clone().sub(playerPos);
    const dist = toNpc.length();
    if (dist >= INTERACT_RANGE || dist < 1e-5) return false;
    return cameraDir.clone().normalize().dot(toNpc.multiplyScalar(1 / dist)) > LOOK_DOT_MIN;
  }

  open(): void {
    if (this.state === 'completed') return;
    this.visible = true;
    this.backdrop.style.display = 'flex';
    this.refreshDialog();
  }

  close(): void {
    this.visible = false;
    this.backdrop.style.display = 'none';
  }

  isOpen(): boolean {
    return this.visible;
  }

  tryOpenFromInteract(playerPos: THREE.Vector3, cameraDir: THREE.Vector3): boolean {
    if (this.state === 'completed') return false;
    if (!this.shouldShowPrompt(playerPos, cameraDir) && !this.isNear(playerPos.x, playerPos.z)) return false;
    this.open();
    return true;
  }

  recordPoiVisit(poiName: string): void {
    if (this.state !== 'accepted') return;
    if (!VISIT_QUEST_POIS.includes(poiName as (typeof VISIT_QUEST_POIS)[number])) return;
    if (this.visited.has(poiName)) return;
    this.visited.add(poiName);
    if (this.visited.size >= VISIT_QUEST_POIS.length) {
      this.state = 'completed';
      this.markerSprite.visible = false;
      this.close();
      this.onComplete();
    }
    this.refreshDialog();
  }

  update(dt: number): void {
    if (this.state === 'completed') return;
    this.bobPhase += dt * 2.2;
    this.markerSprite.position.y = 2.15 + Math.sin(this.bobPhase) * 0.08;
  }

  dispose(): void {
    this.backdrop.remove();
    this.group.removeFromParent();
    this.toonGrad.dispose();
    const m = this.markerSprite.material as THREE.SpriteMaterial;
    m.map?.dispose();
    m.dispose();
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.geometry?.dispose();
        if (o.material instanceof THREE.Material) o.material.dispose();
      }
    });
  }

  private handleAction(): void {
    if (this.state === 'available') {
      this.state = 'accepted';
      this.onAccept();
    }
    this.refreshDialog();
  }

  private refreshDialog(): void {
    const poiList = VISIT_QUEST_POIS.map((p) => (this.visited.has(p) ? `✓ ${p}` : `○ ${p}`)).join('\n');
    if (this.state === 'available') {
      this.bodyEl.textContent =
        `Visit Pleasant Park and Tilted Towers this match.\nReward: ${VISIT_QUEST_MAT_REWARD} mats · +${VISIT_QUEST_SHIELD_REWARD} shield · Shockwave +1`;
      this.actionBtn.textContent = 'Accept Quest';
      this.actionBtn.disabled = false;
    } else if (this.state === 'accepted') {
      this.bodyEl.textContent = `Progress:\n${poiList}`;
      this.actionBtn.textContent = 'Keep exploring…';
      this.actionBtn.disabled = true;
    } else {
      this.bodyEl.textContent = 'Quest complete — safe travels!';
      this.actionBtn.textContent = 'Done';
      this.actionBtn.disabled = true;
    }
  }
}
